import { ChangeDetectorRef, Component, EventEmitter, Injector, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import MasterFilterService2 from '@app/main/master-filter/master-filter.service2';
import ButtonType from '@app/shared/button/button-type';
import AppPermissions from '@shared/AppPermissionNames';
import AppComponentBase from '@shared/common/app-component-base';
import { IDistrictPolygon, IOwnership } from '@shared/common/common-types';
import { OwnershipStatus } from '@shared/service-proxies/service-proxies';
import GoogleMapConstants from '@shared/utils/google-map-constants';
import { isNil } from 'lodash';

import { IOption } from './cascade-selection.component';
import { DISTRICT_COORDINATES } from './district-coordinates';
import { IOfferFilter, OFFER_FILTER_KEYS } from './IOfferFilter';
import { ILocationOption, locationOptions } from './location-options';

interface ICompany {
    name: string;
}

interface IClearValuesObject {
    clearValues();
}

@Component({
    selector: 'app-master-filter',
    templateUrl: './master-filter.component.html',
    styleUrls: ['./master-filter.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
class MasterFilterComponent extends AppComponentBase implements OnInit {

    @Output() selectedRegionsChange: EventEmitter<IOption[]> = new EventEmitter<IOption[]>(); // Event emitter for selected regions

    locationOptions: ILocationOption[] = locationOptions;

    selectedRegions: ILocationOption[] = [];

    filtersOpenState = {
        estateType: false,
        price: false,
        area: false,
        ownership: false,
        location: false,
        company: false,
        advancedFilters: false,
    };

    iconClass: string = 'pi pi-chevron-down';

    defaultPriceLabel: string = 'Цена';

    chosenPriceLabel: string = 'Цена';

    defaultAreaLabel: string = 'Площ (кв/м.)';

    chosenAreaLabel: string = 'Площ (кв/м.)';

    chosenPropertyLabel: string = 'Имот';

    chosenLocationLabel: string = 'Местоположение';

    priceLabel: string = '';

    priceLabelSquare: string = '';

    areaLabel: string = '';

    ownerships: IOwnership[] | undefined;

    selectedOwnership: IOwnership | undefined;

    companies: ICompany[] | undefined;

    selectedCompany: ICompany | undefined;

    loadedResultsCount: number = 0;

    ShowApartmentFilters: boolean = false;

    ShowHouseFilters: boolean = false;

    hasSelection = false;

    // map
    districtsCoordinates = DISTRICT_COORDINATES;

    center: google.maps.LatLngLiteral = { lat: 42.698334, lng: 23.319941 }; // Default to Sofia, Bulgaria

    zoom: number = 13;

    mapOptions: google.maps.MapOptions = GoogleMapConstants.filterMapOptions;

    markers: any[] = [];

    polygons: google.maps.Polygon[] = [];

    polygonModels: IDistrictPolygon[] = [];

    googleMapsLoaded = false;

    tooltip: string;

    form: FormGroup;

    protected readonly ButtonType = ButtonType;

    private map: google.maps.Map;

    private selectedEstateCategoryFirstLevel: IOption;

    private selectedEstateCategorySecondLevel: IOption[] = [];

    private selectedEstateTypes: IOption[] = [];


    constructor(
        injector: Injector,
        private cdr: ChangeDetectorRef,
        private masterFilterService: MasterFilterService2,
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
    ) {
        super(injector);
    }

    get moneyFromControl() {
        return this.form.get('moneyFrom');
    }

    get moneyToControl() {
        return this.form.get('moneyTo');
    }

    get squareMetrePriceFromControl() {
        return this.form.get('squareMetrePriceFrom');
    }

    get squareMetrePriceToControl() {
        return this.form.get('squareMetrePriceTo');
    }

    get squareMetreAreaFromControl() {
        return this.form.get('areaFrom');
    }

    get squareMetreAreaToControl() {
        return this.form.get('areaTo');
    }

    get teamViewPermissions() {
        return [
            AppPermissions.AdministrationFullAccess,
            AppPermissions.OfferTeamView,
        ];
    }

    updateLoadedResultsCount(trueValues: Record<string, unknown>) {
        const { ...rest } = trueValues;
        this.loadedResultsCount = Object.keys(rest).length;
    }

    handleAllValuesCleared() {
        this.loadedResultsCount = 0;
    }

    ngOnInit() {
        this.form = this.fb.group({
            source: [null],
            sourceDetail: [null],
            hasSource: [false],
            period: [null],
            areaFrom: [null],
            areaTo: [null],
            moneyFrom: [null],
            moneyTo: [null],
            squareMetrePriceFrom: [null],
            squareMetrePriceTo: [null],
            estateType: this.fb.group({
                firstLevel: [null],
                secondLevel: [[]],
                estateTypes: [[]],
            }),
        });

        this.companies = [
            { name: 'Всички компании' },
            { name: 'Адрес' },
            { name: 'Имотека' },
            { name: 'New Estates' },
            { name: 'Forton Homes' },
            { name: 'Unique Estates' },
        ];

        this.selectedCompany = this.companies[0];

        this.masterFilterService
            .getOwnershipOptions()
            .subscribe(result => {
                if (!this.isGrantedAny(...this.teamViewPermissions)) {
                    result = result.filter(ownership => ownership.status !== OwnershipStatus.Team);
                }
                this.ownerships = result;

            });

        this.masterFilterService
            .getSelectedOwnership()
            .subscribe((result) => {
                this.selectedOwnership = result;
            });

        this.masterFilterService
            .getSelectedEstateCategoryFirstLevel()
            .subscribe((result) => {
                this.selectedEstateCategoryFirstLevel = result;
                this.updateEstateTypesTitle();
                this.hasSelection = !isNil(this.selectedEstateCategoryFirstLevel);

                this.form.get('estateTypes')
                    ?.get('firstLevel')
                    .patchValue(result.label);
            });

        this.masterFilterService
            .getSelectedEstateCategorySecondLevel()
            .subscribe((result) => {
                if (!isNil(result)) {
                    this.selectedEstateCategorySecondLevel = result;
                    this.updateEstateTypesTitle();
                }
            });

        this.masterFilterService
            .getSelectedEstateTypes()
            .subscribe((result) => {
                if (!isNil(result)) {
                    this.selectedEstateTypes = result;
                    this.updateEstateTypesTitle();
                }
            });

        this.afterMapInit();

        this.masterFilterService
            .getAreaLabel()
            .subscribe((result) => {
                this.chosenAreaLabel = result || this.defaultAreaLabel;
            });

        this.masterFilterService
            .getPriceLabel()
            .subscribe((result) => {
                this.updateRangeLabels(result, 'price');
            });

        this.masterFilterService
            .getPriceSquareLabel()
            .subscribe((result) => {
                this.updateRangeLabels(result, 'priceSquare');
            });

        const paramMap = this.activatedRoute.snapshot.queryParamMap;

        OFFER_FILTER_KEYS.forEach((key) => {
            const valueFromUrl = paramMap.get(key);

            if (!isNil(valueFromUrl)) {
                this.form.patchValue({ [key]: valueFromUrl });
            }
        });

        const firstLevel = paramMap.get('estateCategoriesFirstLevel');
        const secondLevel = paramMap.getAll('estateCategoriesSecondLevel');
        const estateTypes = paramMap.getAll('estateTypes');

        this.form.patchValue({
            estateType: {
                firstLevel,
                secondLevel,
                estateTypes,
            },
        });

        this.emitEstateTypeChange();
    }

    changeOwnership(ownership) {
        this.masterFilterService.updateOwnership(ownership);
        this.onSubmit();
    }

    emitEstateTypeChange(): void {
        const estateType = this.form.get('estateType')?.value;
        if (estateType) {
            this.masterFilterService.getEstateCategoriesFirstLevel()
                .subscribe((firstLevelOptions) => {
                    if (firstLevelOptions) {
                        const selectedFirstLevel = firstLevelOptions.find(option => option.label === estateType.firstLevel);

                        if (selectedFirstLevel) {
                            this.masterFilterService.updateSelectedEstateCategoryFirstLevel(selectedFirstLevel);

                            this.masterFilterService.getEstateCategoriesSecondLevel()
                                .subscribe((secondLevelOptions) => {
                                    const filteredSecondLevels = secondLevelOptions?.filter(option =>
                                        option.parentId === selectedFirstLevel.id && estateType.secondLevel.includes(option.label));

                                    if (filteredSecondLevels.length > 0) {
                                        this.masterFilterService.updateSelectedEstateCategorySecondLevel(filteredSecondLevels);

                                        this.masterFilterService.getEstateTypes()
                                            .subscribe((estateTypeOptions) => {
                                                const filteredEstateTypes = estateTypeOptions?.filter(option =>
                                                    filteredSecondLevels.some(secondLevel => secondLevel.id === option.parentId) &&
                                                    estateType.estateTypes.includes(option.label));

                                                if (filteredEstateTypes.length > 0) {
                                                    this.masterFilterService.updateSelectedEstateTypes(filteredEstateTypes);
                                                }
                                            });
                                    }
                                });
                        }
                    }
                });
        }
    }

    handleAreaChanges(area: { valueFrom: number; valueTo: number }) {
        this.form.get('areaFrom')
            .patchValue(area.valueFrom);
        this.form.get('areaTo')
            .patchValue(area.valueTo);
    }

    handleMoneyFrom(money: { valueFrom: number; valueTo: number }) {
        this.form.get('moneyFrom')
            .patchValue(money.valueFrom);
        this.form.get('moneyTo')
            .patchValue(money.valueTo);
    }


    handlePricePerSquareMetresFrom(pricePerSquareMetresPrice: { valueFrom: number; valueTo: number }) {
        this.form.get('squareMetrePriceFrom')
            .patchValue(pricePerSquareMetresPrice.valueFrom);
        this.form.get('squareMetrePriceTo')
            .patchValue(pricePerSquareMetresPrice.valueTo);
    }

    onSubmit() {
        const rawFilterValues: IOfferFilter = this.form.value as IOfferFilter;
        // Replace fields with value '' with null
        const cleanedFilterValues: IOfferFilter = Object.entries(rawFilterValues)
            .reduce((acc, [key, value]) => {
                acc[key] = value === ''
                    ? null
                    : value;
                return acc;
            }, {} as IOfferFilter);

        this.masterFilterService.getSelectedEstateCategoryFirstLevel()
            .subscribe(x => cleanedFilterValues.estateType.firstLevel = x?.label);

        this.masterFilterService.getSelectedEstateCategorySecondLevel()
            .subscribe(x => cleanedFilterValues.estateType.secondLevel = x?.map(y => y.label));

        this.masterFilterService.getSelectedEstateTypes()
            .subscribe(x => cleanedFilterValues.estateType.estateTypes = x?.map(y => y.label));

        this.masterFilterService.getSelectedOwnership()
            .subscribe(x => cleanedFilterValues.ownership = x);

    }

    onMapInitialized(map: google.maps.Map) {
        this.map = map;
        this.afterMapInit();
    }

    afterMapInit() {
        if (this.map) {
            this.updateMap();
            this.setPolygonsForAllRegions();
        }
    }

    setPolygonsForAllRegions() {
        Object.keys(this.districtsCoordinates)
            .forEach(region => {
                const coordinates = this.districtsCoordinates[region];
                if (coordinates) {
                    // Convert coordinate objects to LatLng objects
                    const paths = coordinates.map(coord => new google.maps.LatLng(coord.lat, coord.lng));
                    const polygon = new google.maps.Polygon({
                        ...GoogleMapConstants.districtPolygonStyle,
                        paths: paths,
                        clickable: true,
                    });

                    // Add the polygon to the map
                    polygon.setMap(this.map);

                    const label = this.getRegionLabel(region);

                    this.polygons.push(polygon);

                    // Store the polygon model in polygonModels array for later use
                    const polygonModel: IDistrictPolygon = {
                        polygon: polygon,
                        district: { id: region, name: label }, // Use the fetched label here
                    };
                    this.polygonModels.push(polygonModel);
                } else {
                    console.warn(`No valid coordinates found for region: ${region}`);
                }
            });
    }

    getRegionLabel(regionId: string): string {

        for (const country of this.locationOptions) {
            if (country.value === regionId) {
                return country.label;
            }

            if (country.children) {
                for (const region of country.children) {
                    if (region.value === regionId) {
                        return region.label;
                    }

                    //matching region at district level
                    if (region.children) {
                        for (const subRegion of region.children) {
                            if (subRegion.value === regionId) {
                                return subRegion.label;
                            }
                        }
                    }
                }
            }
        }

        console.warn(`No label found for regionId: ${regionId}`);
        return regionId;
    }


    updateMap() {
        this.updatePolygonsState();

        if (this.selectedRegions.length === 0) {
            this.setDefaultMapView();
            return;
        }

        const bounds = new google.maps.LatLngBounds();

        this.selectedRegions.forEach(region => {
            const coordinates = this.districtsCoordinates[region.value];
            if (coordinates) {
                coordinates.forEach(coord => {
                    bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
                });
            }
        });

        if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds);
        } else {
            this.setDefaultMapView(); // reset to default map view
        }
    }

    setDefaultMapView() {
        this.map.setCenter(this.center); // Default center based on the city
        this.map.setZoom(this.zoom);
    }

    clearPolygons() {
        this.polygons.forEach(polygon => polygon.setMap(null));
        this.polygons = [];
    }

    onMarkerClick(marker: any) {
        console.log('Marker clicked:', marker);
    }

    onLocationChange(latLng: { lat: number; lng: number }) {
        if (this.map) {
            this.selectedRegions = [];
            this.selectedRegionsChange.emit(this.selectedRegions);
            this.updateMap();
            this.center = latLng;
        } else {
            console.error('Map is not initialized.');
        }
    }


    onPolygonClick(polygonModel: IDistrictPolygon) {

        const selectedRegion = this.selectedRegions.find(region => region.value === polygonModel.district.id);

        if (!selectedRegion) {
            const newRegion: ILocationOption = {
                id: 0,
                value: polygonModel.district.id,
                label: polygonModel.district.name,
            };
            this.selectedRegions.push(newRegion);

            polygonModel.polygon.setOptions({ ...GoogleMapConstants.districtPolygonStyle, fillOpacity: 0.90 });
        } else {
            this.selectedRegions = this.selectedRegions.filter(region => region.value !== polygonModel.district.id);
        }

        this.updateMap();

        this.selectedRegionsChange.emit(this.selectedRegions);
    }


    updatePolygonsState() {

        this.polygonModels.forEach(polygonModel => {
            polygonModel.polygon.setOptions({ ...GoogleMapConstants.districtPolygonStyle });
        });

        // Highlight selected polygons
        this.selectedRegions.forEach(region => {
            const polygonModel = this.polygonModels.find(pm => pm.district.id === region.value);
            if (polygonModel) {
                polygonModel.polygon.setOptions({ ...GoogleMapConstants.districtPolygonStyle, fillOpacity: 0.70 });
            }
        });
    }

    handleFilterChange(newLabel: string) {
        this.chosenLocationLabel = newLabel || 'Местоположение';
        this.cdr.detectChanges();
    }

    updateSelectedRegions(regions: ILocationOption[]) {
        this.selectedRegions = regions;
        this.selectedRegionsChange.emit(this.selectedRegions);
        this.updateMap();
        this.cdr.detectChanges();
    }

    removeRegion(region: ILocationOption, event: MouseEvent) {
        event.stopPropagation(); // Prevent closing the dropdown
        this.selectedRegions = this.selectedRegions.filter(r => r.value !== region.value);
        this.updateSelectedRegions(this.selectedRegions);
    }

    updateDropdownIcon(elementOpen) {
        return elementOpen
            ? 'pi pi-chevron-down rotate-icon rotate-up'
            : 'pi pi-chevron-down rotate-icon';
    }

    updateAdvancedFiltersDropdownIcon(elementOpen) {
        return elementOpen
            ? 'pi pi-chevron-right rotate-icon rotate-down'
            : 'pi pi-chevron-right rotate-icon';
    }

    updateRangeLabels(newLabel: string, type: string) {
        if (type === 'price') {
            this.priceLabel = newLabel;

        } else if (type === 'priceSquare') {
            this.priceLabelSquare = newLabel;
        }
        this.setPriceTitleLabel();
    }

    setPriceTitleLabel() {
        if (this.priceLabel && this.priceLabelSquare) {
            this.chosenPriceLabel = this.priceLabel;
        } else if (this.priceLabel) {
            this.chosenPriceLabel = this.priceLabel;
        } else if (this.priceLabelSquare) {
            this.chosenPriceLabel = this.priceLabelSquare;
        } else {
            this.chosenPriceLabel = 'Цена';
        }


    }

    updateEstateTypesTitle() {
        const labels = [];

        if (this.selectedEstateCategoryFirstLevel) {
            labels.push(this.selectedEstateCategoryFirstLevel?.label);

            if (this.selectedEstateCategorySecondLevel.length > 0) {
                const secondLevelLabels = this.selectedEstateCategorySecondLevel.map(option => option.label);
                labels.push(...secondLevelLabels);
            }

            if (this.selectedEstateTypes.length > 0) {
                const estateTypes = this.selectedEstateTypes?.map(option => option.label);
                labels.push(...estateTypes);
            }
        }

        const { label, tooltip } = this.tooltipTitle(labels, this.chosenPropertyLabel);
        this.chosenPropertyLabel = label;
        this.tooltip = tooltip;
    }


    tooltipTitle(labels: string[], defaultLabel: string): { label: string; tooltip: string } {
        let label = defaultLabel;
        let tooltip = '';

        if (labels.length > 3) {
            label = labels.slice(0, 3)
                .join(', ') + '...';
            tooltip = labels.join(', ');
        } else if (labels.length > 0) {
            label = labels.join(', ');
        }
        return { label, tooltip };
    }

    updateHasSelection() {
        this.hasSelection = isNil(this.selectedEstateCategoryFirstLevel);
    }

    clearSelection(...components: IClearValuesObject[]) {
        // CLEAR FROM MASTER FILTER all selected 1,2, and types
        components.forEach(component => {
            component.clearValues();
        });
    }

    clearEstateTypesSelection() {
        this.masterFilterService.clearEstateCategoriesAndTypes();
        this.chosenPropertyLabel = 'Имот';
    }

    clearSelectionLocation() {
        this.selectedRegions = [];
        this.chosenLocationLabel = 'Местоположение';
    }

    onFilterEstateTypeShow() {
        this.filtersOpenState.estateType = true;
    }

    onFilterEstateTypeHide() {
        this.filtersOpenState.estateType = false;
    }

    onFilterPriceShow() {
        this.filtersOpenState.price = true;
    }

    onFilterPriceHide() {
        this.filtersOpenState.price = false;
    }

    onFilterAreaShow() {
        this.filtersOpenState.area = true;
    }

    onFilterAreaHide() {
        this.filtersOpenState.area = false;
    }

    onFilterOwnershipShow() {
        this.filtersOpenState.ownership = true;
    }

    onFilterOwnershipHide() {
        this.filtersOpenState.ownership = false;
    }

    onFilterLocationShow() {
        this.filtersOpenState.location = true;
    }

    onFilterLocationHide() {
        this.filtersOpenState.location = false;
    }

    onFilterCompanyShow() {
        this.filtersOpenState.company = true;
    }

    onFilterCompanyHide() {
        this.filtersOpenState.company = false;
    }

    toggleAdvancedFilters() {
        this.filtersOpenState.advancedFilters = !this.filtersOpenState.advancedFilters;
    }

    onPropertySelectionChange(propertySelection: string) {
        this.masterFilterService.updateSelectedProperty(propertySelection);
    }

    onPropertyDeselectionChange(propertyDeselection: string) {

        this.masterFilterService.removeSelectedProperty(propertyDeselection);

        if (!this.ShowApartmentFilters && !this.ShowHouseFilters) {
            this.ShowApartmentFilters = false;
            this.ShowHouseFilters = false;
        }

        this.cdr.detectChanges();
    }

}

export default MasterFilterComponent;
